import { Modal } from "~/components/Modal";
import { useInlineAuth } from "../../hooks/useInlineAuth";
import { useRegistrationModal } from "../../hooks/useRegistrationModal";
import { EmailForm } from "../EmailForm";
import { AuthFormCardSeparator } from "../AuthFormCard";
import { OAuthButton } from "../OAuthButton";
import { OTPVerification } from "../OTPVerification";

// // // //

function ModalContent(props: {
    currentStep: "auth" | "otp";
    isLoading: boolean;
    userEmail: string;
    errorMessage: string | null;
    onGoogleSignIn: () => void;
    onEmailSubmit: (email: string) => void;
    onOTPSubmit: (code: string, email: string) => void;
}) {
    const {
        currentStep,
        isLoading,
        userEmail,
        errorMessage,
        onGoogleSignIn,
        onEmailSubmit,
        onOTPSubmit,
    } = props;

    if (currentStep === "auth") {
        return (
            <div className="w-full max-w-md mx-auto min-h-[280px]">
                <div className="text-center mb-6">
                    <h2 className="text-center text-xl font-normal tracking-tight text-slate-800 sm:text-[1.375rem]">
                        Create an account to continue
                    </h2>
                    <p className="mt-2 text-center text-sm leading-relaxed text-secondary-foreground">
                        Sign up to continue exploring real-world asset
                        tokenization.
                    </p>
                </div>

                <div className="mt-5 sm:mt-8 space-y-4 sm:space-y-6">
                    <EmailForm
                        disabled={isLoading}
                        onSubmit={onEmailSubmit}
                        showLoading={isLoading}
                        isWorkEmailOnly={true}
                    />

                    <AuthFormCardSeparator />

                    <OAuthButton
                        provider="google"
                        variant="register"
                        disabled={isLoading}
                        loading={false}
                        onClick={onGoogleSignIn}
                    />
                </div>
            </div>
        );
    }

    if (currentStep === "otp") {
        return (
            <div className="w-full max-w-md mx-auto">
                <div className="text-center mb-6">
                    <h2 className="text-center text-xl font-normal tracking-tight text-slate-800 sm:text-[1.375rem]">
                        Check your email
                    </h2>
                    {errorMessage ? (
                        <p className="mt-2 text-center text-sm leading-relaxed text-red-600">
                            {errorMessage}
                        </p>
                    ) : (
                        <p className="mt-2 text-center text-sm leading-relaxed text-secondary-foreground">
                            A sign-in code has been sent to your email address.
                        </p>
                    )}
                </div>

                <OTPVerification
                    email={userEmail}
                    isLoading={isLoading}
                    onSubmit={onOTPSubmit}
                />
            </div>
        );
    }

    return null;
}

// // // //

/**
 * RegistrationModalLayout
 * Renders the global registration modal driven by the auth store.
 * Mount once near the app root; trigger anywhere via useRegistrationModal().
 */
export function GlobalRegistrationModal() {
    const { isOpen, close } = useRegistrationModal();

    const {
        currentStep,
        isLoading,
        userEmail,
        errorMessage,
        handleEmailFormSubmit,
        handleGoogleSignIn,
        handleOTPSubmit,
    } = useInlineAuth({ onAuthSuccess: close });

    return (
        <Modal
            show={isOpen}
            onHide={close}
            allowClose
            size="md"
            allowScroll
            blurBackground
            viewport="inset"
        >
            <div className="bg-card px-5 py-8">
                <ModalContent
                    currentStep={currentStep}
                    isLoading={isLoading}
                    userEmail={userEmail}
                    errorMessage={errorMessage}
                    onGoogleSignIn={handleGoogleSignIn}
                    onEmailSubmit={handleEmailFormSubmit}
                    onOTPSubmit={handleOTPSubmit}
                />
            </div>
        </Modal>
    );
}
